import React, { useState, useEffect } from 'react';
import { Search, Package, Clock, MapPin, CheckCircle, Truck, AlertCircle, Eye, Download, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import { trackingAPI } from '../services/api';
import TrackingTimeline from './TrackingTimeline';
import BulkTracking from './BulkTracking';

const TrackingPage = () => {
  const [awb, setAwb] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('single'); // 'single' or 'bulk'
  const { toast } = useToast();

  // The delivery estimate reads as a status, so it sits beside the status
  // badge rather than buried in a panel below the fold.
  const deliveryInsight = trackingData?.delivery_insights?.special_instructions?.[0] || '';

  const handleTrackShipment = async (awbNumber = awb) => {
    if (!awbNumber.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter an AWB/tracking number",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      console.log(`[TRACKING] Starting combined track for AWB: ${awbNumber.trim()}`);

      // Try combined tracking first (internal + Priority Connections)
      let result = await trackingAPI.trackCombined(awbNumber.trim());
      console.log(`[TRACKING] Combined API Response:`, result);

      if (result.success) {
        // Priority for display:
        // 1. If internal tracking exists, use it as primary but check for API updates
        // 2. If only API tracking exists, use it

        let displayData = null;

        if (result.internal_tracking) {
          console.log(`[TRACKING] Using internal tracking data:`, result.internal_tracking);
          displayData = result.internal_tracking;

          // Even if we use internal as primary, we can augment it with API metadata if needed
          const pcData = result.priority_connections_tracking?.data;
          if (pcData && pcData.status !== "Not Found" && pcData.status !== "Unknown") {
            // Update status if API is more recent/delivered
            if (pcData.status === "Delivered" && displayData.status !== "delivered") {
              displayData.status = "delivered";
              displayData.progress_percentage = 100;
            }
          }
        } else if (result.priority_connections_tracking?.success) {
          const pcData = result.priority_connections_tracking.data;
          console.log(`[TRACKING] Using Priority Connections data:`, pcData);

          // Helper to parse Priority Connections date "DD-Mon-YYYY @ HH:MM"
          const parsePCDate = (dateStr) => {
            try {
              if (!dateStr) return new Date().toISOString();
              if (dateStr.includes('@')) {
                const [datePart, timePart] = dateStr.split('@').map(s => s.trim());
                return new Date(`${datePart} ${timePart}`).toISOString();
              }
              return new Date(dateStr).toISOString();
            } catch (e) {
              return new Date().toISOString();
            }
          };

          // Format events for timeline
          const formattedEvents = (pcData.tracking_events || []).map(event => ({
            status: event.status || 'Update',
            description: event.message || event.status, // Map message to description
            location: event.location || 'N/A',
            timestamp: parsePCDate(event.timestamp),
            icon: 'truck'
          }));

          displayData = {
            awb: pcData.awb || awbNumber.trim(),
            status: pcData.status?.toLowerCase().replace(/ /g, '_') || 'in_transit',
            raw_status: pcData.status, // Keep original for display
            carrier: 'Priority Connections',
            service_type: 'express',
            progress_percentage: pcData.status?.toLowerCase().includes('delivered') ? 100 :
              pcData.status?.toLowerCase().includes('dispatched') ? 60 :
                pcData.status?.toLowerCase().includes('booked') ? 20 : 50,
            sender: {
              name: 'N/A',
              city: formattedEvents[formattedEvents.length - 1]?.location || 'N/A',
              state: 'N/A'
            },
            recipient: {
              name: 'Receiver',
              city: pcData.current_location || 'N/A',
              state: 'N/A'
            },
            package_info: {
              // PC's tracking feed carries no contents description, and its
              // status message is not one - showing it would be misleading.
              contents: pcData.contents || null,
              declared_value: 0
            },
            tracking_events: formattedEvents,
            delivery_insights: {
              is_delayed: false,
              special_instructions: pcData.tracking_message ? [pcData.tracking_message] : [],
              recommendations: pcData.pod_file ? [`POD Available: ${pcData.pod_file}`] : []
            },
            last_updated: parsePCDate(pcData.last_update),
            pod_file: pcData.pod_file
          };
        }

        if (displayData) {
          setTrackingData(displayData);
          toast({
            title: "Tracking Found",
            description: `Shipment Status: ${displayData.raw_status || displayData.status}`
          });
        } else {
          throw new Error('No tracking records found for this AWB');
        }
      } else {
        throw new Error(result.error || 'Tracking failed');
      }
    } catch (error) {
      console.error('[TRACKING] Error:', error);
      setTrackingData(null);
      toast({
        title: "No Records Found",
        description: "Please check your AWB number and try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTrackShipment();
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('delivered')) return 'bg-green-100 text-green-800';
    if (s.includes('transit') || s.includes('dispatched')) return 'bg-purple-100 text-purple-800';
    if (s.includes('booked')) return 'bg-blue-100 text-blue-800';
    if (s.includes('pickup')) return 'bg-yellow-100 text-yellow-800';

    const statusColors = {
      'booked': 'bg-blue-100 text-blue-800',
      'pickup_scheduled': 'bg-yellow-100 text-yellow-800',
      'picked_up': 'bg-orange-100 text-orange-800',
      'in_transit': 'bg-purple-100 text-purple-800',
      'out_for_delivery': 'bg-indigo-100 text-indigo-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      'returned': 'bg-gray-100 text-gray-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusIcon = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('delivered')) return <CheckCircle className="w-4 h-4" />;
    if (s.includes('transit')) return <Truck className="w-4 h-4" />;

    const icons = {
      'booked': <CheckCircle className="w-4 h-4" />,
      'pickup_scheduled': <Clock className="w-4 h-4" />,
      'picked_up': <Package className="w-4 h-4" />,
      'in_transit': <Truck className="w-4 h-4" />,
      'out_for_delivery': <MapPin className="w-4 h-4" />,
      'delivered': <CheckCircle className="w-4 h-4" />,
      'cancelled': <AlertCircle className="w-4 h-4" />,
      'returned': <RefreshCw className="w-4 h-4" />
    };
    return icons[status] || <Package className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-sm mb-8 border border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="text-center">
            <h1 className="text-3xl font-extrabold text-slate-800 mb-2">Track Your Shipment</h1>
            <p className="text-slate-500 font-medium">Real-time status and complete journey updates</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Tracking Tabs */}
        <div className="flex justify-center mb-10">
          <div className="flex bg-slate-100/50 p-1.5 rounded-xl border border-slate-200 shadow-inner">
            <button
              onClick={() => setActiveTab('single')}
              className={`px-8 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${activeTab === 'single'
                ? 'bg-orange-500 text-white shadow-md transform scale-105'
                : 'text-slate-500 hover:text-slate-800'
                }`}
            >
              Single Tracking
            </button>
            <button
              onClick={() => setActiveTab('bulk')}
              className={`px-8 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 ${activeTab === 'bulk'
                ? 'bg-orange-500 text-white shadow-md transform scale-105'
                : 'text-slate-500 hover:text-slate-800'
                }`}
            >
              Bulk Tracking
            </button>
          </div>
        </div>

        {activeTab === 'single' ? (
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Single Tracking Form */}
            <Card className="border-none shadow-xl shadow-slate-200/50 rounded-2xl overflow-hidden p-2 bg-white/40 backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      placeholder="Enter AWB Number (e.g. A1041908)"
                      value={awb}
                      onChange={(e) => setAwb(e.target.value.toUpperCase())}
                      onKeyPress={handleKeyPress}
                      className="text-lg h-14 pl-12 bg-white/80 border-slate-100 rounded-xl focus:ring-orange-500 focus:border-orange-500 font-mono"
                    />
                  </div>
                  <Button
                    onClick={() => handleTrackShipment()}
                    disabled={loading}
                    className="bg-orange-500 hover:bg-orange-600 h-14 px-10 rounded-xl text-lg font-bold transition-all hover:shadow-lg hover:shadow-orange-200 active:scale-95"
                  >
                    {loading ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      "Track Now"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Tracking Results */}
            {trackingData && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                {/* Shipment Overview */}
                <Card className="border-none shadow-xl shadow-slate-200/50 rounded-2xl overflow-hidden">
                  <CardHeader className="border-b border-slate-50 bg-slate-50/30">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                          {getStatusIcon(trackingData.status)}
                        </div>
                        <CardTitle className="text-xl font-bold text-slate-800">Shipment Details</CardTitle>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {deliveryInsight && (
                          <Badge className="bg-orange-50 text-orange-800 border border-orange-100 hover:bg-orange-50 text-xs font-bold px-3 py-1 rounded-full tracking-wider whitespace-nowrap">
                            {deliveryInsight}
                          </Badge>
                        )}
                        <Badge className={`${getStatusColor(trackingData.status)} text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider whitespace-nowrap`}>
                          {trackingData.raw_status || formatStatus(trackingData.status)}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                      <div className="space-y-1">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">AWB Number</h4>
                        <p className="text-lg font-mono font-bold text-slate-800">{trackingData.awb}</p>
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Carrier</h4>
                        <p className="text-lg font-bold text-slate-800">{trackingData.carrier}</p>
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Service Type</h4>
                        <p className="text-lg font-bold text-slate-800 uppercase">{trackingData.service_type}</p>
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Progress</h4>
                        <div className="pt-2">
                          <Progress value={trackingData.progress_percentage} className="h-2 bg-orange-100" />
                          <p className="text-xs font-bold text-slate-500 mt-2">{trackingData.progress_percentage}% Complete</p>
                        </div>
                      </div>
                    </div>

                    {/* Address Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-10 pt-10 border-t border-slate-50">
                      <div className="space-y-4">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center">
                          <MapPin className="w-3 h-3 mr-2 text-slate-300" /> Origin Site
                        </h4>
                        <div className="p-4 bg-slate-50/50 rounded-xl border border-slate-100">
                          <p className="text-lg font-bold text-slate-800">{trackingData.sender.city}</p>
                          <p className="text-sm text-slate-500">{trackingData.sender.name}</p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center">
                          <MapPin className="w-3 h-3 mr-2 text-slate-300" /> Current Site
                        </h4>
                        <div className="p-4 bg-slate-50/50 rounded-xl border border-slate-100">
                          <p className="text-lg font-bold text-slate-800">{trackingData.recipient.city}</p>
                          <p className="text-sm text-slate-500">Processing Hub</p>
                        </div>
                      </div>
                    </div>

                    {/* Package Information & Insights */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-10 pt-10 border-t border-slate-50">
                      <div className="space-y-4">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Information</h4>
                        <div className="space-y-3">
                          {trackingData.package_info?.contents ? (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-slate-500 font-medium">Contents:</span>
                              <span className="font-bold text-slate-800 truncate max-w-[200px]">{trackingData.package_info.contents}</span>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400 italic">Shipment details not available for this AWB.</p>
                          )}
                        </div>
                      </div>
                      {trackingData.pod_file && (
                        <div className="space-y-4">
                          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Proof of Delivery</h4>
                          <Button
                            onClick={() => window.open(trackingData.pod_file, '_blank')}
                            variant="outline"
                            className="w-full border-slate-200 font-bold"
                          >
                            <Download className="w-4 h-4 mr-2" /> Download Proof (POD)
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Tracking Timeline */}
                <div className="bg-white/40 backdrop-blur-sm p-4 rounded-3xl border border-white/20 shadow-xl shadow-slate-200/40">
                  <TrackingTimeline
                    trackingEvents={trackingData.tracking_events}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <BulkTracking />
        )}
      </div>
    </div>
  );
};

export default TrackingPage;